import React, { useRef, useState, useEffect } from "react";
import { Play, Pause } from "lucide-react";

/**
 * Always-visible song player. Placeholder MP3 src — replace later.
 * Vinyl spins continuously as ambient animation; progress bar animates when playing.
 */
export const SongPlayer = ({ src = "" }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    const onTime = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
      }
    };
    const onEnd = () => setPlaying(false);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
    };
  }, [src]);

  // Aggressively preload audio on mount so it's ready when user interacts
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !src) return;
    audio.preload = "auto";
    try {
      audio.load();
    } catch (e) {
      // ignore
    }
  }, [src]);

  // Auto-start on first user gesture anywhere on the page (synchronous, gesture-preserving)
  const playingRef = useRef(false);
  useEffect(() => {
    if (!src) return;
    const tryStart = () => {
      const audio = audioRef.current;
      if (!audio || playingRef.current) return;
      const p = audio.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          playingRef.current = true;
          setPlaying(true);
          cleanup();
        }).catch(() => {
          // gesture rejected; will retry on next gesture
        });
      } else {
        playingRef.current = true;
        setPlaying(true);
        cleanup();
      }
    };
    const events = ["pointerdown", "click", "keydown", "wheel", "touchstart", "mousemove"];
    const opts = { capture: true, passive: true };
    events.forEach((e) => window.addEventListener(e, tryStart, opts));
    // Also attempt right away in case the browser allows it (returning user, MEI etc.)
    tryStart();
    function cleanup() {
      events.forEach((e) => window.removeEventListener(e, tryStart, opts));
    }
    return cleanup;
  }, [src]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio || !src) {
      setPlaying((p) => !p);
      return;
    }
    if (playing) {
      audio.pause();
      playingRef.current = false;
      setPlaying(false);
    } else {
      try {
        await audio.play();
        playingRef.current = true;
        setPlaying(true);
      } catch (e) {
        setPlaying(false);
      }
    }
  };

  // Fake progress when no audio src so the bar still animates visibly
  useEffect(() => {
    if (src || !playing) return;
    const id = setInterval(() => {
      setProgress((p) => (p >= 100 ? 0 : p + 0.25));
    }, 200);
    return () => clearInterval(id);
  }, [playing, src]);

  return (
    <div className="song-player" data-testid="song-player">
      {src && <audio ref={audioRef} src={src} loop preload="auto" />}

      <button
        type="button"
        onClick={toggle}
        className="vinyl-btn"
        aria-label={playing ? "Pause" : "Play"}
        data-testid="song-play-toggle"
      >
        <div className={`vinyl ${playing ? "spinning" : "idle-spin"}`}>
          <div className="vinyl-ring" />
          <div className="vinyl-ring vinyl-ring--mid" />
          <div className="vinyl-center" />
        </div>
        <div className="vinyl-icon">
          {playing ? <Pause size={14} strokeWidth={2.5} /> : <Play size={14} strokeWidth={2.5} />}
        </div>
      </button>

      <div className="song-meta">
        <div className="song-title">Abhi Na Jao Chhod Ke</div>
        <div className="song-artist">Lata Mangeshkar &amp; Mohammed Rafi</div>
        <div className="song-bar">
          <div
            className="song-bar-fill"
            style={{ width: `${progress}%` }}
            data-testid="song-progress"
          />
        </div>
      </div>
    </div>
  );
};

export default SongPlayer;
